import Link from 'next/link';
import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import Logo from 'components/atoms/Logo';
import HeaderMenuToggle from 'components/atoms/HeaderMenuToggle';
import { useKeypress } from 'hooks/useKeypress';
import { MAIN_NAVIGATION } from 'navigation/mainNavigation';
import { compareUrlsBySpecificity } from 'utils/links';
import { SkipToMainContentButton } from 'components/molecules/SkipToMainContent';

import SideMenu from './SideMenu';

const isErrorPath = (path: string) =>
  path.startsWith('/404') || path.startsWith('/_error');

const MAIN_NAVIGATION_BY_SPECIFICITY = MAIN_NAVIGATION.toSorted((navA, navB) =>
  compareUrlsBySpecificity(navB.href, navA.href)
);

const isMostSpecificSelectedPath = (currentPath: string, comparePath: string) =>
  MAIN_NAVIGATION_BY_SPECIFICITY.find((link) =>
    currentPath.startsWith(link.href)
  )?.href === comparePath;

const Header: FC = () => {
  const { asPath, route } = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useKeypress(
    'Escape',
    () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    },
    [isMenuOpen]
  );

  return (
    <nav className="grid items-center grid-cols-[minmax(min-content,1fr)_minmax(min-content,max-content)_1fr] px-8 py-4">
      <div className="flex items-center mr-8">
        <HeaderMenuToggle
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
        />
        <Logo />
        <SideMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      </div>
      <SkipToMainContentButton />
      <ul className="justify-center hidden w-full __header:flex">
        {MAIN_NAVIGATION.map((link) => (
          <li
            key={link.href}
            className="mr-4 tablet:mr-4 laptop:mr-6 desktop:mr-10 last:mr-0"
          >
            {link.isNewTab ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  'font-medium text-d-caption block w-full text-center',
                  {
                    'text-hrg-red-100':
                      !isErrorPath(route) &&
                      isMostSpecificSelectedPath(asPath, link.href),
                  }
                )}
              >
                {link.text}
              </a>
            ) : (
              <Link href={link.href} legacyBehavior>
                <a
                  className={clsx(
                    'font-medium text-d-caption block w-full text-center',
                    {
                      'text-hrg-red-100':
                        !isErrorPath(route) &&
                        isMostSpecificSelectedPath(asPath, link.href),
                    }
                  )}
                >
                  {link.text}
                </a>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default Header;
